import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ForwardIcon } from "../components/icons";
import { bookKey, getBook, listVoices, updateBook, type Book, type VoiceCatalogue } from "../api";
import DirectionPicker from "../components/DirectionPicker";
import ModelPicker from "../components/ModelPicker";
import MutationError from "../components/MutationError";
import PartSettings, { DEFAULT_PART_CHARS } from "../components/PartSettings";
import VoicePicker from "../components/VoicePicker";
import { useT, type Messages } from "../i18n";

/**
 * NarrationSettings is how a book is narrated: its voice, its speech model,
 * and how its chapters are cut into parts (ADR-0028, ADR-0029, ADR-0030).
 *
 * They are book settings — a book read by two voices is not a book — and
 * each choice saves on the book the moment it is made. It costs nothing and
 * changes no audio that exists, so there is nothing to confirm. The same
 * component sits on the book's settings screen and, folded away, on every
 * chapter page, where somebody is about to spend money on it.
 */
export default function NarrationSettings({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // A changed setting is a different estimate on every chapter of the book.
  const saved = () => {
    void client.invalidateQueries({ queryKey: ["book", bookID] });
    void client.invalidateQueries({ queryKey: ["estimate"] });
  };
  const chooseVoice = useMutation({
    mutationFn: (voice: string) => updateBook(bookID, { default_voice: voice }),
    onSuccess: saved,
  });
  const chooseModel = useMutation({
    mutationFn: (model: string) => updateBook(bookID, { narration_model: model }),
    onSuccess: saved,
  });
  const chooseParts = useMutation({
    mutationFn: (patch: { chunk_chars?: number; read_context?: boolean }) =>
      updateBook(bookID, patch),
    onSuccess: saved,
  });
  const chooseMultiSpeaker = useMutation({
    mutationFn: (multi_speaker: boolean) => updateBook(bookID, { multi_speaker }),
    onSuccess: () => {
      saved();
      void client.invalidateQueries({ queryKey: ["script"] });
    },
  });
  const chooseDirection = useMutation({
    mutationFn: (narration_direction: string) => updateBook(bookID, { narration_direction }),
    onSuccess: saved,
  });

  const b = book.data;
  if (!b) return null;
  const modelInfo = catalogue.data?.models?.find(
    (m) => m.name === (b.narration_model || catalogue.data?.default_model),
  );

  return (
    <div data-testid="narration-settings">
      <p className="mt-3 text-xs text-muted">{t.narration.settingsNote}</p>
      <VoicePicker
        value={b.default_voice ?? ""}
        model={b.narration_model ?? ""}
        disabled={chooseVoice.isPending}
        onChange={(voice) => chooseVoice.mutate(voice)}
      />
      {/* Voices for each character: English books on a model that performs
          (ADR-0157, ADR-0064). */}
      {modelInfo?.performs && (b.language ?? "").toLowerCase().startsWith("en") && (
        <Link
          to={`/studio/books/${bookID}/voices`}
          data-testid="open-voices"
          className="mt-3 flex min-h-11 items-center justify-between gap-2 rounded-control border border-line px-3 py-2 text-sm transition-colors hover:bg-sunk"
        >
          <span>
            <span className="block font-medium">{t.voices.open}</span>
            <span className="block text-xs text-muted">{t.voices.openNote}</span>
          </span>
          <ForwardIcon className="size-4 text-muted" />
        </Link>
      )}
      <ModelPicker
        value={b.narration_model ?? ""}
        language={b.language ?? ""}
        disabled={chooseModel.isPending}
        onChange={(model) => chooseModel.mutate(model)}
      />
      {/* Shown only where the model takes direction: one that is sent nothing
          but the text would speak a direction rather than follow it, and the
          server refuses the field there, so this is showing the rule rather
          than keeping it (ADR-0109 §4). */}
      {modelInfo && !modelInfo.no_direction && (
        <DirectionPicker
          // Starts again from the saved value whenever the server's changes.
          key={b.narration_direction ?? ""}
          value={b.narration_direction ?? ""}
          disabled={chooseDirection.isPending}
          onChange={(direction) => chooseDirection.mutate(direction)}
        />
      )}
      <PartSettings
        // Starts again from the saved values whenever the server's change.
        key={`${b.chunk_chars ?? 0}-${b.read_context ?? false}-${modelInfo?.max_part_chars ?? 0}`}
        chunkChars={b.chunk_chars ?? 0}
        readContext={b.read_context ?? false}
        maxChars={modelInfo?.max_part_chars}
        offersReadContext={!modelInfo?.takes_no_lead_in}
        disabled={chooseParts.isPending}
        onChunkChars={(chunk_chars) => chooseParts.mutate({ chunk_chars })}
        onReadContext={(read_context) => chooseParts.mutate({ read_context })}
      />
      {/* Offered only where it can work: an English book on a model that
          performs (ADR-0157 §8). The server refuses it anywhere else. */}
      {modelInfo?.performs && b.language === "en" && (
        <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-ink-soft select-none">
          <input
            type="checkbox"
            data-testid="multi-speaker"
            checked={b.multi_speaker ?? false}
            disabled={chooseMultiSpeaker.isPending}
            onChange={(e) => chooseMultiSpeaker.mutate(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            {t.script.multiSpeakerLabel}
            <span className="block text-xs text-muted">{t.script.multiSpeakerNote}</span>
          </span>
        </label>
      )}
      {/* Every control here is derived from the saved book, so a refusal snaps
          the picker back to the old value. Without a line saying why, that
          reads as a control that does not work. */}
      <MutationError
        className="mt-2"
        testId="setting-error"
        error={
          chooseVoice.error ??
          chooseModel.error ??
          chooseDirection.error ??
          chooseParts.error ??
          chooseMultiSpeaker.error
        }
        fallback={t.narration.settingChangeFailed}
      />
    </div>
  );
}

/** narrationSummary is the settings in one line: voice, model, part size. */
export function narrationSummary(
  book: Book,
  catalogue: VoiceCatalogue | undefined,
  t: Messages,
): string {
  const modelName = book.narration_model || catalogue?.default_model || "";
  const model = catalogue?.models?.find((m) => m.name === modelName);
  // The model's voices, then the book's choice, then the model's own default
  // (ADR-0109). A one-voice model is narrated in that voice whatever the book
  // chose, which is why its list wins here.
  const chosen = book.default_voice || model?.default_voice || catalogue?.default || "";
  const only = model?.voices?.length === 1 ? model.voices[0] : undefined;
  const found = model?.voices?.find((v) => v.name === chosen);
  // A voice not in the catalogue is one designed for this book (ADR-0157).
  const designed = !!model?.performs && !!book.default_voice && !found;
  const voice = designed
    ? t.voice.designed
    : (only ?? found)?.label || (only ?? found)?.name || chosen || "…";
  const cap = model?.max_part_chars ?? Infinity;
  const chars = Math.min(book.chunk_chars || DEFAULT_PART_CHARS, cap);
  const label = t.voice.models[modelName] ?? model?.label ?? modelName;
  return t.narration.settingsSummary(voice, label, chars);
}
